import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Lightbulb, BookOpen, Users, ArrowRight, Star, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Testimonials data
const testimonials = [
  {
    id: 1,
    name: "Mike Thompson",
    role: "Father of 3",
    text: "Dad Connect transformed how I spend time with my kids. The activity ideas are creative and actually work!",
    rating: 5
  },
  {
    id: 2,
    name: "James Rodriguez",
    role: "Father of 2",
    text: "The coaching sessions gave me tools I use every single day. Worth every penny.",
    rating: 5
  },
  {
    id: 3,
    name: "David Chen",
    role: "Father of 1",
    text: "As a first-time dad, this resource has been invaluable. I feel so much more confident now.",
    rating: 5
  }
];

const Home = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/homepage`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching homepage content:', error);
      // Use defaults if fetch fails
      setContent({});
    } finally {
      setLoading(false);
    }
  };

  // Default values
  const c = {
    hero_title: content?.hero_title || 'Build Awesome Connections',
    hero_subtitle: content?.hero_subtitle || 'With Your Kids! 🎉',
    hero_description: content?.hero_description || 'Discover tons of fun activities, cool life skills, and amazing moments that make you the dad your kids will always remember!',
    hero_button_text: content?.hero_button_text || "Let's Go! 🚀",
    hero_button_link: content?.hero_button_link || '/activities',
    features_title: content?.features_title || 'Everything You Need to Be an',
    features_subtitle: content?.features_subtitle || 'Amazing Dad!',
    feature_1_title: content?.feature_1_title || 'Fun Activities',
    feature_1_description: content?.feature_1_description || 'Hundreds of awesome activities organized by age and interest',
    feature_2_title: content?.feature_2_title || 'Easy Guides',
    feature_2_description: content?.feature_2_description || 'Step-by-step instructions that actually make sense!',
    feature_3_title: content?.feature_3_title || 'Parenting Tips',
    feature_3_description: content?.feature_3_description || 'Real advice for tough conversations and tricky situations',
    feature_4_title: content?.feature_4_title || 'Dad Coaching',
    feature_4_description: content?.feature_4_description || '1-on-1 help from Andrew Garcia, family expert with 20+ years experience',
    cta_title: content?.cta_title || 'Ready to Be an Awesome Dad? 🎉',
    cta_description: content?.cta_description || 'Start exploring fun activities today or get personalized help through our coaching program!',
    cta_button_text: content?.cta_button_text || 'Get Started Free! 🚀',
    cta_button_link: content?.cta_button_link || '/signup',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-teal-50 to-green-100 -z-10" />
        
        {/* Playful decorative elements */}
        <div className="absolute top-10 left-10 w-24 h-24 bg-blue-300 rounded-full opacity-30 blur-2xl animate-pulse" />
        <div className="absolute top-40 right-20 w-32 h-32 bg-teal-300 rounded-full opacity-30 blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-green-300 rounded-full opacity-30 blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-8">
            <h1 className="text-6xl md:text-8xl font-black leading-tight tracking-tight">
              <img 
                src="https://customer-assets.emergentagent.com/job_dadtime-guides/artifacts/y6hj3s71_Dad%20connect%20logo.png" 
                alt="Dad Connect" 
                className="h-32 md:h-48 w-auto mx-auto mb-8"
              />
              <span className="block text-4xl md:text-6xl text-gray-900">
                {c.hero_title}
              </span>
              <span className="block text-4xl md:text-6xl bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent mt-2">
                {c.hero_subtitle}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto font-medium">
              {c.hero_description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={c.hero_button_link}>
                <Button size="lg" className="text-lg px-8 py-6 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 hover:from-blue-600 hover:via-teal-600 hover:to-green-600 text-white shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 font-bold">
                  {c.hero_button_text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/coaching">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-4 border-teal-500 text-teal-700 hover:bg-teal-50 font-bold">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              {c.features_title}
              <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent"> {c.features_subtitle}</span>
            </h2>
            <p className="max-w-2xl !text-xl mx-auto text-gray-600">
              From the littles to the bigs, we've got tons of fun stuff for every age!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Lightbulb,
                title: c.feature_1_title,
                description: c.feature_1_description,
                color: 'from-yellow-400 to-orange-400',
                link: '/activities'
              },
              {
                icon: BookOpen,
                title: c.feature_2_title,
                description: c.feature_2_description,
                color: 'from-blue-400 to-teal-400',
                link: '/resources'
              },
              {
                icon: Heart,
                title: c.feature_3_title,
                description: c.feature_3_description,
                color: 'from-pink-400 to-red-400',
                link: '/self-improvement'
              },
              {
                icon: Users,
                title: c.feature_4_title,
                description: c.feature_4_description,
                color: 'from-green-400 to-teal-400',
                link: '/coaching'
              }
            ].map((feature, index) => (
              <Link to={feature.link} key={index}>
                <Card className="border-4 border-gray-200 hover:border-teal-400 transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2 hover:rotate-1 cursor-pointer group bg-gradient-to-br from-white to-gray-50 h-full">
                  <CardContent className="p-6 space-y-4">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-200 shadow-lg`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Age Groups Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Activities for Every Age! 🎈
            </h2>
            <p className="text-xl text-gray-600">
              From baby giggles to teenage talks - we've got you covered!
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {[
              { age: 'Newborn-2', emoji: '🍼', color: 'from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300', border: 'border-blue-400', label: 'Baby Activities' },
              { age: '3-5 Years', emoji: '🎨', color: 'from-teal-100 to-teal-200 hover:from-teal-200 hover:to-teal-300', border: 'border-teal-400', label: 'Preschool Fun' },
              { age: '6-10 Years', emoji: '⚽', color: 'from-green-100 to-green-200 hover:from-green-200 hover:to-green-300', border: 'border-green-400', label: 'Elementary' },
              { age: '11-13 Years', emoji: '🎮', color: 'from-cyan-100 to-cyan-200 hover:from-cyan-200 hover:to-cyan-300', border: 'border-cyan-400', label: 'Middle School' },
              { age: '14-18 Years', emoji: '🎸', color: 'from-indigo-100 to-indigo-200 hover:from-indigo-200 hover:to-indigo-300', border: 'border-indigo-400', label: 'High School' }
            ].map((group, index) => (
              <Link to="/activities" key={index} className="w-full sm:w-64">
                <Card className={`bg-gradient-to-br ${group.color} border-4 ${group.border} transition-all duration-300 hover:shadow-2xl transform hover:scale-110 hover:-rotate-3 cursor-pointer h-full`}>
                  <CardContent className="p-8 text-center space-y-3">
                    <div className="text-6xl" style={{ animationDelay: `${index * 0.1}s` }}>{group.emoji}</div>
                    <h3 className="font-black text-lg">{group.age}</h3>
                    <p className="text-sm text-gray-600">{group.label}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              What Dads Are Saying! 💬
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of fathers building awesome connections!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-4 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-2xl transform hover:-rotate-1 bg-gradient-to-br from-white to-teal-50">
                <CardContent className="p-6 space-y-4">
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic font-medium">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-500 via-teal-500 to-green-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-2xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-2xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <h2 className="text-4xl md:text-5xl font-black">
            {c.cta_title}
          </h2>
          <p className="text-xl opacity-95 font-medium">
            {c.cta_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={c.cta_button_link}>
              <Button size="lg" className="text-lg px-8 py-6 bg-white text-teal-600 hover:bg-gray-100 shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-200 font-bold">
                {c.cta_button_text}
              </Button>
            </Link>
            <Link to="/coaching">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-4 border-white text-white hover:bg-white hover:text-teal-600 font-bold">
                Learn About Coaching
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
